"use client";

import { useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";

import { FUOCO_HOME } from "@/components/home/stile";

/**
 * La parte che si clicca della Fig. 1 di /osint (§ 4.4 delle istruzioni
 * `oi-istruzioni-osint 02.md`, `.vs` / `.vd` del modello
 * `uploads/osint 01.html`): una verità alla volta, come il manuale.
 *
 * Il grafico e le sei schede arrivano già resi dal server (`grafico`,
 * `schede`): qui sta solo quale verità è scelta. Le schede sono tutte
 * nell'HTML, le chiuse con `display: none`, così a JavaScript spento si
 * legge la prima e il testo delle altre resta in pagina. All'apertura è
 * scelta la prima, Inquinamento.
 *
 * - I sei bottoni (`.vs-b`) sono il comando vero, con `aria-pressed`.
 * - Le bande del grafico si cliccano anche loro: il clic arriva qui per
 *   delega, dal `data-v` della `.os-vhit` colpita, senza che il disegno
 *   diventi client. Sono una scorciatoia col mouse; da tastiera si usano i
 *   bottoni, che stanno subito sotto.
 * - Il riquadro rame sulla banda scelta (`.os-vsel`) si sposta con la
 *   custom property `--os-scelta` scritta sul contenitore (vedi globals.css).
 *
 * Sotto i 900 il grafico resta largo 820 e scorre dentro la sua cornice,
 * il selettore va su due colonne e la scheda su una.
 */
export function SelettoreVerita({
  grafico,
  voci,
  schede,
}: {
  grafico: ReactNode;
  /** Etichetta della banda (SEDIMENTO…) e titolo della voce, nell'ordine. */
  voci: readonly { etichetta: string; titolo: string }[];
  schede: ReactNode[];
}) {
  const [scelta, setScelta] = useState(0);

  const daGrafico = (e: MouseEvent<HTMLDivElement>) => {
    if (!(e.target instanceof Element)) return;
    const v = e.target.closest("[data-v]")?.getAttribute("data-v");
    if (v != null) setScelta(Number(v));
  };

  return (
    <div style={{ "--os-scelta": scelta } as CSSProperties}>
      {/* Il clic sulle bande è un doppione dei bottoni sotto, per il mouse:
          l'elemento non è interattivo per la tastiera di proposito. */}
      <div className="min-w-0 max-[899px]:overflow-x-auto" onClick={daGrafico}>
        {grafico}
      </div>

      <div
        role="group"
        aria-label="Scegli una verità"
        className="grid grid-cols-2 border-t border-ink min-[900px]:grid-cols-6"
      >
        {voci.map((v, i) => {
          const on = i === scelta;
          return (
            <button
              key={v.titolo}
              type="button"
              aria-pressed={on}
              onClick={() => setScelta(i)}
              className={`flex cursor-pointer flex-col gap-[3px] border-0 border-b-[3px] border-[color-mix(in_srgb,var(--oi-sv-linea)_20%,transparent)] px-2.5 pt-2 pb-2.5 text-left font-serif text-[14px] leading-[1.25] text-ink transition-colors min-[900px]:px-3 min-[900px]:pt-2.5 min-[900px]:pb-3 min-[900px]:text-[15px] ${BORDI_BOTTONE[i]} ${
                on
                  ? "border-b-segnale bg-[color-mix(in_srgb,var(--color-segnale)_8%,var(--color-surface))]"
                  : "border-b-transparent bg-surface hover:bg-[color-mix(in_srgb,var(--oi-sv-linea)_6%,var(--color-surface))]"
              } ${FUOCO_HOME}`}
            >
              <span className="font-mono text-[10px] tracking-[0.12em] text-segnale min-[900px]:text-[11px]">
                {v.etichetta}
              </span>
              {v.titolo}
            </button>
          );
        })}
      </div>

      <div className="border-t border-ink bg-surface">
        {schede.map((s, i) => (
          <div
            key={voci[i]!.titolo}
            className={`grid-cols-1 gap-3.5 px-4 pt-[18px] pb-[18px] min-[900px]:grid-cols-[240px_minmax(0,1fr)] min-[900px]:gap-8 min-[900px]:px-[26px] min-[900px]:pt-6 min-[900px]:pb-[26px] ${
              i === scelta ? "grid" : "hidden"
            }`}
          >
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}

/** I filetti fra i bottoni (`.vs-b`): a sinistra fra uno e l'altro da 900
 *  (sei in fila), sotto i 900 due colonne, quindi niente filetto a sinistra
 *  sui dispari e un filetto sopra dalla seconda riga in poi. Colori in
 *  classi intere, perché Tailwind non genera classi composte a runtime. */
const BORDI_BOTTONE = [
  "",
  "border-l",
  "max-[899px]:border-t min-[900px]:border-l",
  "border-l max-[899px]:border-t",
  "max-[899px]:border-t min-[900px]:border-l",
  "border-l max-[899px]:border-t",
];
